import type {ISurveyData, ISurveyFiltersData, ISurveyOfferDetails, ISurveyPropertyDetails} from "../../types/IBuildSurveyParams";
import {surveyCalculatePrice} from "./survey_calculate_price";
import {surveyCalculateRoomsRange} from "./survey_calculate_rooms_range";
import {surveyMergeRegions} from "./survey_merge_regions";

export type BuildSurveyResult = ISurveyData | Partial<ISurveyData>;

const doFiltersOrSurveyExist = (filters: ISurveyFiltersData | null, survey: ISurveyData | null): boolean => {
    const hasFilters = filters && filters.filters && Object.keys(filters.filters).length > 0;
    const hasSurvey = survey !== null;

    return Boolean(hasFilters) || hasSurvey;
};

/**
 * Builds an updated survey by merging data from property propertyDetails, offerDetails propertyDetails, filters and existing survey.
 *
 * Four scenarios:
 * 1. Only property propertyDetails (new user): elevate region to district/county, exact rooms, price + 10%
 * 2. Only offerDetails propertyDetails (new user): elevate region to district/county, rooms from offerDetails range, max price + 10%
 * 3. Filters + property/offerDetails: merge regions, widest rooms range, max price + 10%
 * 4. Survey + property/offerDetails: add region, expand rooms range, raise price only if property/offerDetails is more expensive
 */
export const buildSurvey = (
    propertyDetails: ISurveyPropertyDetails | null,
    offerDetails: ISurveyOfferDetails | null,
    filters: ISurveyFiltersData | null,
    survey: ISurveyData | null
): BuildSurveyResult => {
    const isEmptyDetails = !propertyDetails || Object.keys(propertyDetails).length === 0;
    const isEmptyOffer = !offerDetails || Object.keys(offerDetails).length === 0;
    const isEmptyFilters = !filters || Object.keys(filters).length === 0;

    // If we have survey and both propertyDetails/offerDetails and filters are empty objects, return survey unchanged
    if (survey && isEmptyDetails && isEmptyOffer && isEmptyFilters) {
        return survey;
    }

    const hasOtherSources = doFiltersOrSurveyExist(filters, survey);
    const hasSurvey = survey !== null;

    const regions = surveyMergeRegions(survey?.regions, propertyDetails?.offer?.region, offerDetails?.region, filters?.filters?.region, hasOtherSources);

    const rooms =
        isEmptyDetails && isEmptyOffer && isEmptyFilters && survey
            ? survey.rooms
            : surveyCalculateRoomsRange(
                  survey?.rooms,
                  propertyDetails?.rooms,
                  offerDetails?.roomsMin,
                  offerDetails?.roomsMax,
                  filters?.filters?.rooms_0,
                  filters?.filters?.rooms_1,
                  hasOtherSources
              );

    const price =
        isEmptyDetails && isEmptyOffer && isEmptyFilters && survey
            ? survey.price
            : surveyCalculatePrice(survey?.price, propertyDetails?.price, offerDetails?.priceMax, filters?.filters?.price_1, hasSurvey);

    return {
        ...(survey || {}),
        regions,
        rooms,
        price
    };
};
